import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Paper,
  Typography,
} from "@mui/material";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import GoogleAuth from "../../components/frontend/auth/GoogleAuth";
import LoginForm from "../../components/frontend/auth/LoginForm";
import { useAuthActions } from "../../hooks/useAuthActions";
import { useAuthNavigation } from "../../hooks/useAuthNavigation";

const LoginPage: React.FC = () => {
  const { t: myTranslate } = useTranslation();
  const { login, googleAuth, isLoading, errorMessage } = useAuthActions();
  const {
    navigateToDestination,
    navigateToRegister,
    navigateToForgotPassword,
    navigateToRegisterPage,
  } = useAuthNavigation();

  // Handle successful login
  useEffect(() => {
    if (login.isSuccess) {
      navigateToDestination();
    }
  }, [login.isSuccess]);

  // Handle successful Google auth
  useEffect(() => {
    if (googleAuth.isSuccess && googleAuth.data?.data) {
      const data = googleAuth.data.data;

      if (data.isNewUser) {
        navigateToRegister(data);
      } else {
        navigateToDestination();
      }
    }
  }, [googleAuth.isSuccess, googleAuth.data]);

  // Handle form submission
  const handleLoginSubmit = (data: { email: string; password: string }) => {
    login.mutate(data);
  };

  // Handle Google auth
  const handleGoogleAuth = (payload: {
    data: any;
    type: "login" | "register";
  }) => {
    if (payload.type === "login") {
      googleAuth.mutate({ idToken: payload.data.credential });
    }
  };

  const handleGoogleError = () => {
    console.error("Google auth error");
    // Error is handled by the hook
  };

  // Show loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: "100%",
          maxWidth: 400,
          borderRadius: 2,
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          className="notranslate"
          translate="no"
          gutterBottom
          align="center"
          sx={{ mb: 3 }}
        >
          {myTranslate("auth.welcomeBack")}
        </Typography>

        <Typography
          variant="body2"
          className="notranslate"
          translate="no"
          color="text.secondary"
          sx={{ mb: 3, textAlign: "center" }}
        >
          {myTranslate("auth.signInToContinue")}
        </Typography>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorMessage}
          </Alert>
        )}

        {/* Google Auth */}
        <GoogleAuth
          type="login"
          onSuccess={handleGoogleAuth}
          onError={handleGoogleError}
        />

        <Divider sx={{ my: 3 }}>
          <Box
            component="span"
            className="notranslate"
            translate="no"
            sx={{ color: "text.secondary" }}
          >
            {myTranslate("auth.or")}
          </Box>
        </Divider>

        {/* Email/Password Login Form */}
        <LoginForm
          onSubmit={handleLoginSubmit}
          isLoading={isLoading}
          onForgotPassword={navigateToForgotPassword}
          onRegister={navigateToRegisterPage}
        />
      </Paper>
    </Box>
  );
};

export default LoginPage;
